import { type Component, type JSXElement, Show } from "solid-js";
import { Portal } from "solid-js/web";
import SparklingIcon from "~/Icons/32_Icon_Sparkling.svg?component-solid";
import { getUserStore } from "~/solidJs/entities/HeaderMain/models";
import { Button, ModalV2Ui } from "~/solidJs/shared";
import { formatCurrency } from "~/solidJs/shared/helpers/currencyHelpers";
import { parseDateToReadableString } from "~/solidJs/shared/helpers/dateHelpers";
import { StyledSelect } from "~/solidJs/shared/ui/molecules/Select";
import {
	ConfirmPlanChangeContext,
	getConfirmPlanChangeModalState,
	showConfirmPlanChangeModal,
	useConfirmPlanChangeStore,
	useConfirmPlanContext,
	usePlanChangeDetails
} from "./methods";
import type { ChangeSubscriptionPeriod } from "./model";

const PlanPeriodSelect = () => {
	const store = useConfirmPlanContext();

	const handlePeriodChange = (value: ChangeSubscriptionPeriod[]) => {
		if (value.length > 0) {
			store.setSubscriptionPeriodByItem(value[0]);
		}
	};

	return (
		<div class="flex w-full flex-col gap-2 pb-6">
			<StyledSelect.SelectClosedComponent
				label="Choose your subscription change"
				rootProps={{
					multiple: false,
					size: "lg",
					class: "w-full",
					positioning: { sameWidth: true },
					onValueChange: (d) => handlePeriodChange(d.items)
				}}
				triggerProps={{ class: "w-full justify-between" }}
				items={store.getAvailablePeriods()}
				value={[store.getSelectedPeriodItem()]}
			/>
			<span class="caption-11-medium text-text-tertiary">
				{store.getSelectedPeriodItem()?.description}
			</span>
		</div>
	);
};

type NamedDetailItemProps = {
	label: JSXElement;
	detailElement: JSXElement;
};

const NamedDetailItem: Component<NamedDetailItemProps> = (props) => {
	return (
		<div class="flex w-full">
			<div class="typography-text-14-medium flex-1 text-text-secondary-v2">{props.label}</div>
			<div class="flex-1">{props.detailElement}</div>
		</div>
	);
};

type DetailsInfoProps = {
	value: JSXElement;
	description: JSXElement;
};

const DetailsInfo: Component<DetailsInfoProps> = (props) => {
	return (
		<div class="flex flex-col items-end gap-1">
			<div class="header-18-semibold w-full text-right text-text-primary">{props.value}</div>
			<div class="label-10-regular text-text-tertiary">{props.description}</div>
		</div>
	);
};

const TotalDueInfo = () => {
	const store = useConfirmPlanContext();
	const paymentQuery = usePlanChangeDetails();

	const amount = () => {
		if (paymentQuery.isLoading) return "Loading...";
		if (paymentQuery.isError) return "Error";
		return getUserStore()?.subscription === "stripe"
			? formatCurrency(paymentQuery.data?.amount.toString() || "0", "USD")
			: formatCurrency(paymentQuery.data?.amount.toString() || "0", "RUB");
	};

	const description = () => {
		if (paymentQuery.isLoading) return "Loading...";
		return store.getSelectedPeriodItem().chargeDescription;
	};

	return (
		<NamedDetailItem
			label="Total due"
			detailElement={<DetailsInfo value={amount()} description={description()} />}
		/>
	);
};

const NextBillingInfo = () => {
	const paymentQuery = usePlanChangeDetails();

	const nextBillingDate = () => {
		if (paymentQuery.isLoading) return "Loading...";
		if (paymentQuery.isError) return "August 15";

		const data = paymentQuery.data;
		if (!data) return "No data";

		const paymentDate = new Date(data.nextPaymentDate);
		return parseDateToReadableString(paymentDate);
	};

	const billingAmount = () => {
		if (paymentQuery.isLoading) return "Loading...";
		if (paymentQuery.isError) return "Error";

		const data = paymentQuery.data;
		if (!data) return "No data";

		return getUserStore()?.subscription === "stripe"
			? `$${formatCurrency(data?.amount.toString() || "0", "USD")}`
			: `${formatCurrency(data?.amount.toString() || "0", "RUB")}`;
	};

	return (
		<NamedDetailItem
			label="Upgrade date"
			detailElement={
				<DetailsInfo
					value={<span class="typography-text-14-semibold">{nextBillingDate()}</span>}
					description={billingAmount()}
				/>
			}
		/>
	);
};

interface ConfirmPlanChangeModalProps {
	heading?: JSXElement;
	actionButtons: JSXElement;
}

export const ConfirmPlanChangeModal: Component<ConfirmPlanChangeModalProps> = (props) => {
	const store = useConfirmPlanContext();

	return (
		<Show when={getConfirmPlanChangeModalState()}>
			<Portal>
				<ModalV2Ui.ModalWithOverlayBoilerPlate
					autoClose
					toggleSelf={() => store.hideModal()}
				>
					<ModalV2Ui.Header showCloseButton toggleSelf={() => store.hideModal()}>
						<h2 class="header-30-semibold text-text-primary">
							{props.heading || "Confirm downgrade"}
						</h2>
					</ModalV2Ui.Header>
					<ModalV2Ui.ContentContainer class="flex-col">
						<PlanPeriodSelect />
						<div class="flex w-full flex-col gap-4 border-border-secondary border-t py-4">
							<TotalDueInfo />
							<NextBillingInfo />
						</div>
					</ModalV2Ui.ContentContainer>

					<ModalV2Ui.ButtonContainer>{props.actionButtons}</ModalV2Ui.ButtonContainer>
				</ModalV2Ui.ModalWithOverlayBoilerPlate>
			</Portal>
		</Show>
	);
};

const DowngradeButtons = () => {
	const store = useConfirmPlanContext();
	return (
		<>
			<Button size="large" onClick={() => store.hideModal()}>
				Cancel
			</Button>
			<Button
				variant="DangerOverlay"
				size="large"
				pending={store.confirmMutation.isPending}
				onClick={() => store.confirmMutation.mutate()}
			>
				Downgrade
			</Button>
		</>
	);
};

const UpgradeButtons = () => {
	const store = useConfirmPlanContext();
	return (
		<>
			<Button size="large" onClick={() => store.hideModal()}>
				Cancel
			</Button>
			<Button
				variant="PrimaryAccent"
				size="large"
				pending={store.confirmMutation.isPending}
				onClick={() => store.confirmMutation.mutate()}
			>
				Upgrade
			</Button>
		</>
	);
};

const SucsessModal = () => {
	const store = useConfirmPlanContext();
	return (
		<Show when={store.getIsModalOpen() && store.getIsShowSuccessModal()}>
			<Portal>
				<ModalV2Ui.ModalWithOverlayBoilerPlate
					autoClose
					toggleSelf={() => store.hideModal()}
				>
					<ModalV2Ui.Header showCloseButton toggleSelf={() => store.hideModal()}>
						<ModalV2Ui.HeaderTextContainer>
							<div class="flex gap-2">
								<SparklingIcon class="text-interaction-violet" />
								<h2 class="header-30-semibold text-text-primary">Success</h2>
							</div>
							<p class="caption-13-medium text-text-secondary-v2">
								{store.getModalMode() === "downgrade" ? "Downgrade" : "Upgrade"}{" "}
								confirmed. {store.getSelectedPeriodItem().successDescription}
							</p>
						</ModalV2Ui.HeaderTextContainer>
					</ModalV2Ui.Header>
					<ModalV2Ui.ContentContainer class="flex-col">
						<div class="flex w-full flex-col gap-4 border-border-secondary border-t pt-4">
							<NextBillingInfo />
						</div>
					</ModalV2Ui.ContentContainer>

					<ModalV2Ui.ButtonContainer>
						<Button
							onClick={() => store.hideModal()}
							variant="PrimaryAccent"
							size="large"
						>
							Great!
						</Button>
					</ModalV2Ui.ButtonContainer>
				</ModalV2Ui.ModalWithOverlayBoilerPlate>
			</Portal>
		</Show>
	);
};

// Provider wrapper component
export const ConfirmPlanChangeModalProvider = () => {
	const store = useConfirmPlanChangeStore();

	return (
		<ConfirmPlanChangeContext.Provider value={store}>
			<Show
				when={store.getIsShowSuccessModal()}
				fallback={
					<ConfirmPlanChangeModal
						heading={
							store.getModalMode() === "downgrade"
								? "Confirm downgrade"
								: "Confirm upgrade"
						}
						actionButtons={
							store.getModalMode() === "downgrade" ? (
								<DowngradeButtons />
							) : (
								<UpgradeButtons />
							)
						}
					/>
				}
			>
				<SucsessModal />
			</Show>
		</ConfirmPlanChangeContext.Provider>
	);
};

export { showConfirmPlanChangeModal };

// Export default as the standalone version for easy global usage
export default ConfirmPlanChangeModalProvider;
